import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  amsterdamDate,
  capacityColor,
  fetchCalendarDepartures,
  toSlotsByRoom,
  type BookingSlot,
} from '../services/bookings';

// The full day, opened by tapping the "Straks geboekt" strip. Same data source
// and the same colour ramp as the strip, just without the "upcoming only" cut,
// so the operator can answer "how busy is this afternoon" and "when could I fit
// this group in" without leaving the dashboard for the booking admin.
//
// Read-only on purpose: nothing here books, moves or cancels anything. The
// operator app authenticates by operator code on the anon key, and the writing
// paths all live behind the admin's JWT edge functions.

interface Props {
  locationId: string;
  onClose: () => void;
}

/** Both directions are capped: this reads a day at a time, and wandering weeks
 *  into the future is the booking admin's job, not a dashboard's. */
const MAX_DAYS_AHEAD = 14;
const MAX_DAYS_BACK = 1;

function shiftDate(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function longDate(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Amsterdam',
  });
}

export default function BookingCalendarModal({ locationId, onClose }: Props) {
  const today = useMemo(() => amsterdamDate(), []);
  const [date, setDate] = useState(today);
  const [byRoom, setByRoom] = useState<Map<string, BookingSlot[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const offset = useMemo(() => {
    const a = Date.parse(`${date}T00:00:00Z`);
    const b = Date.parse(`${today}T00:00:00Z`);
    return Math.round((a - b) / 86_400_000);
  }, [date, today]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchCalendarDepartures(locationId, date, date);
      setByRoom(toSlotsByRoom(rows));
    } catch (err) {
      console.error('booking calendar fetch failed', err);
      setByRoom(new Map());
      setError('Kon de kalender niet ophalen.');
    } finally {
      setLoading(false);
    }
  }, [locationId, date]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Per room here, unlike the strip. The strip answers "is there space at all"
  // and so totals the location; this is the look-closer view, where a two-room
  // venue wants to see which room the space is actually in.
  const rooms = useMemo(() => [...byRoom.entries()], [byRoom]);
  const allSlots = useMemo(() => [...byRoom.values()].flat(), [byRoom]);

  const nowKey = useMemo(() => {
    if (date !== today) return null;
    const now = Date.now();
    // The slot in progress: the last one that has already started.
    let current: string | null = null;
    for (const s of allSlots) {
      if (Date.parse(s.slotStart) <= now) current = s.timeOfDay;
    }
    return current;
  }, [allSlots, date, today]);

  const dayTotals = useMemo(() => {
    const cap = allSlots.reduce((n, s) => n + s.capacity, 0);
    const booked = allSlots.reduce((n, s) => n + s.booked, 0);
    return { cap, booked };
  }, [allSlots]);

  return (
    <div className="isheet-overlay" onClick={onClose} role="presentation">
      <div
        className="isheet bkcal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Boekingskalender"
      >
        <div className="isheet-head">
          <div className="isheet-head-top">
            <span className="isheet-pc">{longDate(date)}</span>
            {dayTotals.cap > 0 ? (
              <span className="isheet-state">{dayTotals.booked} van {dayTotals.cap} plekken geboekt</span>
            ) : null}
            <span className="isheet-spacer" />
            <button
              type="button"
              className="isheet-mini"
              disabled={offset <= -MAX_DAYS_BACK}
              onClick={() => setDate((d) => shiftDate(d, -1))}
            >
              ‹ vorige
            </button>
            <button
              type="button"
              className="isheet-mini"
              disabled={date === today}
              onClick={() => setDate(today)}
            >
              vandaag
            </button>
            <button
              type="button"
              className="isheet-mini"
              disabled={offset >= MAX_DAYS_AHEAD}
              onClick={() => setDate((d) => shiftDate(d, 1))}
            >
              volgende ›
            </button>
            <button type="button" className="isheet-close" onClick={onClose} aria-label="Sluiten">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="bkcal-body">
          {loading ? (
            <div className="isheet-quiet">Kalender laden…</div>
          ) : error ? (
            <div className="isheet-error">{error}</div>
          ) : rooms.length === 0 ? (
            <div className="isheet-quiet">Niets geboekt op deze dag.</div>
          ) : (
            rooms.map(([room, list]) => (
              <section key={room} className="isheet-section">
                <div className="isheet-label">{room}</div>
                <div className="bkcal-grid">
                  {list.map((s) => (
                    <div
                      key={s.key}
                      className={`bkcal-slot${s.timeOfDay === nowKey ? ' bkcal-slot--now' : ''}`}
                      title={s.experiencesLabel ?? 'niets geboekt'}
                    >
                      <div className="bkcal-time">{s.timeOfDay}</div>
                      <div className="bkcal-bar">
                        <span
                          style={{
                            width: `${Math.max(3, Math.round(s.ratio * 100))}%`,
                            background: capacityColor(s.ratio),
                          }}
                        />
                      </div>
                      <div className="bkcal-n">{s.booked}/{s.capacity}</div>
                    </div>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>

        <div className="isheet-foot bkcal-foot">
          <span className="isheet-quiet">
            Alleen kijken. Boeken en verzetten blijft in de boekingsadmin.
          </span>
        </div>
      </div>
    </div>
  );
}
